// 規則式 AI 策略實作
// 對應 PRD §7.2 出牌策略、§7.3 反應策略
//
// 第二期保留既有棄牌評分，補上吃、碰、明槓與自動宣告聽牌。
// 吃／碰採「副露後完整面子數必須增加」的輕量判斷；不移植 Android 的完整查表 AI、
// 有效牌剩餘量與商業勝率操控。IAIPlayer 介面維持可替換，未來仍能再提升防守與牌效。

import { PlayerActionType, type TPlayerAction } from '@core/events';
import { removeFromConcealed, type THand } from '@core/hand';
import { canHu } from '@core/hu';
import {
  DiscardReactionType,
  findDiscardReactions,
  findKongAddedOption,
  findKongConcealedOptions,
} from '@core/ruleset';
import { isTenpai } from '@core/tenpai';
import type { TTile } from '@core/tile';
import type { TRng, TSeat } from '@core/types';

import type { TAIContext, IAIPlayer } from './ai-player';
import { countCompleteMeldsAfterClaim, countHandCompleteMelds } from './hand-progress';
import { evaluateTileForDiscard } from './tile-eval';

/**
 * 從 hand.concealed 挑出一張要打的牌。
 *
 * 演算法：
 * 1. 對每張暗牌計算「該打分數」（evaluateTileForDiscard）
 * 2. 取分數最高那張
 * 3. 若有多張並列最高，用 RNG 隨機挑（PRD §7.2 第 8 條 fallback）
 *
 * 注意：永遠回傳一個 TTile。空手牌會丟錯（理論上不該發生）。
 */
const pickTileToDiscard = (hand: THand, rng: TRng): TTile => {
  const tiles = hand.concealed;
  if (tiles.length === 0) {
    throw new Error('pickTileToDiscard: hand.concealed is empty');
  }

  // 一次掃完，記錄最高分與所有並列最高分的 tile
  let bestScore = -Infinity;
  let bestTiles: TTile[] = [];
  for (const t of tiles) {
    const score = evaluateTileForDiscard(hand, t);
    if (score > bestScore) {
      bestScore = score;
      bestTiles = [t];
    } else if (score === bestScore) {
      bestTiles.push(t);
    }
  }

  if (bestTiles.length === 1) return bestTiles[0] as TTile;

  // 多張並列：隨機挑
  const idx = Math.floor(rng() * bestTiles.length);
  return bestTiles[idx] as TTile;
};

/** AI 出牌後若成聽，把宣告附在同一次出牌動作，交由核心依正確事件順序落定。 */
const buildDiscardAction = (hand: THand, tile: TTile, alreadyDeclared: boolean): TPlayerAction => {
  const afterDiscard = removeFromConcealed(hand, tile);
  const declareTenpai = !alreadyDeclared && isTenpai(afterDiscard);
  return {
    type: PlayerActionType.Discard,
    tile,
    ...(declareTenpai ? { declareTenpai: true } : {}),
  };
};

/**
 * 規則式 IAIPlayer 實作。
 *
 * 透過 createRuleStrategyAI(rng) 建立，注入 RNG 確保測試可重現。
 */
export const createRuleStrategyAI = (rng: TRng): IAIPlayer => ({
  decideTurn(hand: THand, drawnTile: TTile | null, ctx: TAIContext): TPlayerAction {
    // 吃／碰後沒有摸牌：只能從現有暗牌中打出一張，不可自摸或宣告任何自家槓。
    if (!drawnTile) {
      const discard = pickTileToDiscard(hand, rng);
      return buildDiscardAction(hand, discard, ctx.declaredTenpai === true);
    }

    // 規則 1：能胡就胡（自摸）
    if (canHu(hand, drawnTile, true)) {
      return { type: PlayerActionType.Hu };
    }

    // 已宣告聽牌後鎖定手牌：不能再吃碰槓，只能自摸或打出剛摸的牌。
    if (ctx.declaredTenpai) {
      return { type: PlayerActionType.Discard, tile: drawnTile };
    }

    // 規則 2：暗槓必槓（PRD §7.3）— 多組可暗槓時挑第一個
    const concealedKongs = findKongConcealedOptions(hand);
    if (concealedKongs.length > 0) {
      return {
        type: PlayerActionType.KongConcealed,
        option: concealedKongs[0] as (typeof concealedKongs)[0],
      };
    }

    // 規則 3：加槓必槓（PRD 沒明寫但與「暗槓一律槓」邏輯一致）
    const addedKong = findKongAddedOption(hand, drawnTile);
    if (addedKong) {
      return { type: PlayerActionType.KongAdded, option: addedKong };
    }

    // 規則 4：出牌
    const discard = pickTileToDiscard(hand, rng);
    return buildDiscardAction(hand, discard, false);
  },

  decideReaction(
    hand: THand,
    discardedTile: TTile,
    fromSeat: TSeat,
    ctx: TAIContext
  ): TPlayerAction {
    // 規則 1：可胡 100% 胡（PRD §7.3）
    if (canHu(hand, discardedTile, false)) {
      return { type: PlayerActionType.Hu };
    }

    if (ctx.declaredTenpai) return { type: PlayerActionType.Pass };

    const reactions = findDiscardReactions(hand, discardedTile, fromSeat, ctx.mySeat);

    // 明槓不需要拆牌效：與 Android 基本規則一致，能槓就槓。
    const exposedKong = reactions.find(
      (reaction) => reaction.type === DiscardReactionType.KongExposed
    );
    if (exposedKong?.type === DiscardReactionType.KongExposed) {
      return { type: PlayerActionType.KongExposed, option: exposedKong.option };
    }

    // 吃／碰只在「副露後完整面子數嚴格增加」時採用，避免為了能碰就拆掉既有順子。
    const before = countHandCompleteMelds(hand);
    const useful = reactions
      .filter(
        (reaction) =>
          reaction.type === DiscardReactionType.Pong || reaction.type === DiscardReactionType.Chi
      )
      .map((reaction) => ({
        reaction,
        complete: countCompleteMeldsAfterClaim(hand, reaction.option),
      }))
      .filter((candidate) => candidate.complete > before)
      .sort((a, b) => {
        if (a.complete !== b.complete) return b.complete - a.complete;
        return a.reaction.type === DiscardReactionType.Pong ? -1 : 1;
      })[0];

    if (useful?.reaction.type === DiscardReactionType.Pong) {
      return { type: PlayerActionType.Pong, option: useful.reaction.option };
    }
    if (useful?.reaction.type === DiscardReactionType.Chi) {
      return { type: PlayerActionType.Chi, option: useful.reaction.option };
    }

    return { type: PlayerActionType.Pass };
  },
});
